import {cx} from './cx';

/**
 * `−  n  +`.
 *
 * # 48×48, on both axes, and the row is drawn around it
 *
 * The design draws 28×28. `test/guest-defects.test.tsx` asserts the computed
 * `min-width` **and** `min-height` of these two buttons, because a
 * `min-width: 44px` sat on the `−` of a priced modifier row from the first
 * commit until it was found: `+` goes inert at the cap while `−` stays live, so
 * a horizontal thumb slip silently *removed* a paid modifier from a real bill.
 * That is why the assertion is on the cascade rather than on a class name, and
 * why this component sets both axes explicitly.
 *
 * If the row does not fit two 48px buttons, the row is redrawn. The control is
 * not shrunk.
 *
 * # Decrementing past the minimum is not this component's decision
 *
 * In the design, `−` at quantity 1 deletes the line — a destructive action
 * wearing a neutral control's clothes, sitting next to an explicit delete button
 * that does the same thing. So `min` defaults to **1** and `−` disables there.
 * A caller who genuinely wants zero-removes-the-line passes `min={0}` and owns
 * the consequence; the cart should use the labelled delete instead.
 *
 * # Labels
 *
 * `decreaseLabel` / `increaseLabel` are localised and **must name the thing**:
 * "Fewer · Extra shot", not "Fewer". A screen with four modifier rows otherwise
 * announces eight identically named buttons. `valueLabel` names the readout for
 * assistive technology, which otherwise hears a bare number between two buttons.
 */
export function QtyStepper({
  value,
  onChange,
  min = 1,
  max,
  decreaseLabel,
  increaseLabel,
  valueLabel,
  className,
}: {
  value: number;
  onChange: (next: number) => void;
  /** Defaults to 1. Pass 0 only where reaching zero is meant to remove the line. */
  min?: number;
  /** Omit for unbounded. Pass the contract's `max_select` where there is one. */
  max?: number;
  decreaseLabel: string;
  increaseLabel: string;
  valueLabel: string;
  className?: string;
}) {
  const canDecrease = value > min;
  const canIncrease = max === undefined || value < max;

  return (
    <div className={cx('pk-qty', className)}>
      <button
        type="button"
        className="pk-qty-btn"
        aria-label={decreaseLabel}
        disabled={!canDecrease}
        onClick={() => onChange(value - 1)}
      >
        {/* Real minus, not a hyphen: at 16px a hyphen is a third the width of
            the plus above it and reads as a dash. */}
        <span aria-hidden="true">−</span>
      </button>
      <output className="pk-qty-value" aria-label={valueLabel}>
        {value}
      </output>
      <button
        type="button"
        className="pk-qty-btn"
        aria-label={increaseLabel}
        disabled={!canIncrease}
        onClick={() => onChange(value + 1)}
      >
        <span aria-hidden="true">+</span>
      </button>
    </div>
  );
}
